import { useEffect, useRef, useState, type FormEvent } from 'react';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../auth/AuthProvider';
import { functions } from '../lib/firebase';
import { invoiceUnlockRequest, type InvoiceUnlockRequest } from '../lib/invoiceUnlock';
import { useJobFinanceStatus } from '../lib/jobFinance';
import { canUnlockInvoices, type Job } from '../types/models';
import { Alert } from './ui/Alert';
import { Button } from './ui/Button';
import { Field, TextArea } from './ui/Field';
import { Modal } from './ui/Modal';

export function UnlockInvoiceModal({ companyId, job, onClose, onUnlocked }: {
  companyId: string;
  job: Job;
  onClose: () => void;
  onUnlocked: () => void;
}) {
  const { user, activeCompanyId, activeRole } = useAuth();
  const finance = useJobFinanceStatus();
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);
  const scope = JSON.stringify([user?.uid, activeCompanyId, job.id, activeRole]);
  const initialScope = useRef(scope);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const allowed = !!user && scope === initialScope.current && activeCompanyId === companyId
    && canUnlockInvoices(activeRole ?? undefined) && finance.ready && job.accountingStatus === 'invoiced';

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!allowed || saving) return;
    let request: InvoiceUnlockRequest;
    try { request = invoiceUnlockRequest(companyId, job.id, reason); }
    catch (e) { setError((e as Error).message); return; }
    setSaving(true);
    setError(null);
    try {
      await httpsCallable<InvoiceUnlockRequest, { success: boolean }>(functions, 'unlockJobInvoice')(request);
      if (mounted.current) onUnlocked();
    } catch (e) {
      // The server's explanation must reach the person unchanged.
      if (mounted.current) setError(typeof (e as Error)?.message === 'string' ? (e as Error).message : 'Unable to unlock the invoice.');
    } finally {
      if (mounted.current) setSaving(false);
    }
  }

  return (
    <Modal open onClose={onClose} dismissible={!saving} title="Unlock this invoice?"
      description="This returns the job to priced so its price can be changed and it can be marked invoiced again.">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Reason" hint="3 to 500 characters. The reason is kept in the history.">
          <TextArea value={reason} onChange={event => setReason(event.target.value)} required maxLength={500}
            disabled={saving} rows={4} autoFocus />
        </Field>
        {error && <Alert tone="error">{error}</Alert>}
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="submit" variant="secondary" loading={saving} disabled={!allowed || reason.trim().length < 3}>Unlock</Button>
        </div>
      </form>
    </Modal>
  );
}
